import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow, format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, History, User } from "lucide-react";

type Kind = "idea" | "project";

interface Props {
  kind: Kind;
  id: string;
}

const FIELD_LABEL: Record<string, string> = {
  title: "Title",
  description: "Description",
  category: "Category",
  tags: "Tags",
  status: "Status",
  source_type: "Source type",
  source_url: "Source URL",
  source_label: "Source label",
  image_url: "Cover image",
  idea_type: "Idea type",
  grant_details_id: "Grant-ready brief",
};

function fieldValue(snapshot: any, field: string): string {
  if (!snapshot) return "(none)";
  const v = snapshot[field];
  if (v === null || v === undefined || v === "") return "(empty)";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "(empty)";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

const RevisionRow = ({ rev, isLast }: { rev: any; isLast: boolean }) => {
  const [open, setOpen] = useState(false);
  const fields: string[] = Array.isArray(rev.changed_fields) ? rev.changed_fields : [];
  const isCreation = rev.change_kind === "created";
  const name = rev.actor?.display_name ?? "A member";
  const when = new Date(rev.changed_at);

  return (
    <div className="relative pl-8 pb-6">
      {/* Timeline rail */}
      <span
        className="absolute left-2.5 top-2 h-2.5 w-2.5 rounded-full bg-primary ring-4 ring-background"
        aria-hidden
      />
      {!isLast && (
        <span
          className="absolute left-[13px] top-4 bottom-0 w-px bg-border"
          aria-hidden
        />
      )}

      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-1">
        <span className="text-sm font-medium text-foreground">{name}</span>
        {isCreation && (
          <Badge variant="outline" className="text-[10px] py-0 h-4">
            Created
          </Badge>
        )}
        {rev.change_kind === "status_change" && (
          <Badge variant="outline" className="text-[10px] py-0 h-4">
            Status
          </Badge>
        )}
        {rev.change_kind === "published" && (
          <Badge className="text-[10px] py-0 h-4 bg-primary/15 text-primary border-primary/30">
            Published
          </Badge>
        )}
        <span
          className="text-xs text-muted-foreground"
          title={format(when, "PPpp")}
        >
          {formatDistanceToNow(when, { addSuffix: true })}
        </span>
      </div>

      <p className="text-sm text-muted-foreground mb-2">
        {rev.diff_summary ?? (isCreation ? "Initial version." : "Summary pending…")}
      </p>

      {fields.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {fields.map((f) => (
            <Badge key={f} variant="secondary" className="text-[11px]">
              {FIELD_LABEL[f] ?? f}
            </Badge>
          ))}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <ChevronDown className="h-3 w-3 mr-1" /> : <ChevronRight className="h-3 w-3 mr-1" />}
            {open ? "Hide changes" : "Show changes"}
          </Button>
        </div>
      )}

      {open && fields.length > 0 && (
        <div className="mt-3 space-y-3 rounded-lg border border-border bg-card/60 p-3">
          {fields.map((f) => (
            <div key={f} className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                  {FIELD_LABEL[f] ?? f} — before
                </div>
                <div className="whitespace-pre-wrap text-muted-foreground/90">
                  {fieldValue(rev.prior_snapshot, f)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-primary mb-1">
                  {FIELD_LABEL[f] ?? f} — after
                </div>
                <div className="whitespace-pre-wrap text-foreground">
                  {fieldValue(rev.new_snapshot, f)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const HistoryTab = ({ kind, id }: Props) => {
  const table = kind === "idea" ? "idea_revisions" : "project_revisions";
  const parentKey = kind === "idea" ? "idea_id" : "project_id";

  const { data: revisions, isLoading } = useQuery({
    queryKey: ["provenance", kind, id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from(table)
        .select("*")
        .eq(parentKey, id)
        .order("revision_number", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
    enabled: !!id,
  });

  const actorIds = useMemo(() => {
    const s = new Set<string>();
    (revisions ?? []).forEach((r) => r.changed_by && s.add(r.changed_by));
    return Array.from(s);
  }, [revisions]);

  const { data: profiles } = useQuery({
    queryKey: ["provenance-actors", actorIds],
    queryFn: async () => {
      if (actorIds.length === 0) return {};
      const { data } = await supabase
        .from("profiles")
        .select("user_id, display_name, avatar_url")
        .in("user_id", actorIds);
      const map: Record<string, any> = {};
      (data ?? []).forEach((p) => (map[p.user_id] = p));
      return map;
    },
    enabled: actorIds.length > 0,
  });

  const withActors = useMemo(
    () =>
      (revisions ?? []).map((r) => ({
        ...r,
        actor: r.changed_by ? profiles?.[r.changed_by] : null,
      })),
    [revisions, profiles],
  );

  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 animate-pulse h-40" />
    );
  }

  if (!withActors.length) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center">
        <History className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">
          No history recorded yet.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center gap-2 mb-5">
        <History className="h-4 w-4 text-primary" />
        <h3 className="font-semibold text-sm uppercase tracking-wide">Track record</h3>
        <span className="text-xs text-muted-foreground">
          · {withActors.length} {withActors.length === 1 ? "revision" : "revisions"}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-5">
        Every meaningful edit is timestamped and attributed so funders and partners can
        see how this {kind} evolved.
      </p>
      <div>
        {withActors.map((r, i) => (
          <RevisionRow key={r.id} rev={r} isLast={i === withActors.length - 1} />
        ))}
      </div>
    </div>
  );
};

export default HistoryTab;